import {
  type CompleteWarehouseTaskBody,
  type WarehouseLocation,
  type WarehouseTask,
  type WarehouseTaskType,
  api,
} from '@/api/client'
import { HttpError } from '@/api/core'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuthStore } from '@/stores/auth.store'
import { cn } from '@/lib/cn'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useWarehouseSite } from './SiteSwitcher'
import { AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type DeskMode = 'receive' | 'handover' | 'putaway' | 'control' | 'pick'

const PERM: Record<DeskMode, string> = {
  receive: 'warehouse:task:receive',
  handover: 'warehouse:task:handover',
  putaway: 'warehouse:task:putaway',
  control: 'warehouse:task:control',
  pick: 'warehouse:task:pick',
}

function can(perms: readonly string[], code: string) {
  return perms.some((p) => p === '*:*' || p === code || p === 'warehouse:*')
}

export function TaskDesk({ mode, taskType }: { mode: DeskMode; taskType: WarehouseTaskType }) {
  const { t } = useTranslation('warehouse')
  const perms = useAuthStore((s) => s.permissions)
  const allowed = can(perms, PERM[mode])
  const canAssign = can(perms, 'warehouse:task:assign')
  const qc = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('')
  const [notes, setNotes] = useState('')
  const [reason, setReason] = useState('')
  const [locationId, setLocationId] = useState('')
  const [decision, setDecision] = useState<CompleteWarehouseTaskBody['decision']>('in_stock')
  const [newQty, setNewQty] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const { warehouse } = useWarehouseSite()
  const tasks = useQuery({
    queryKey: ['warehouse', 'tasks', taskType, warehouse?.id],
    queryFn: () => api.warehouse.listTasks({ type: taskType, warehouse_id: warehouse!.id }),
    enabled: !!warehouse,
  })

  const list = tasks.data ?? []
  const current = list.find((task) => task.id === selected) ?? list[0] ?? null

  useEffect(() => {
    if (current) {
      setQuantity(current.quantity != null ? String(current.quantity) : String(current.quantity_current ?? ''))
      setNotes(current.notes ?? '')
      setReason('')
      setFormError(null)
    }
  }, [current?.id])

  const locations = useQuery({
    queryKey: ['warehouse', 'locations', current?.warehouse_id],
    queryFn: () => api.warehouse.listLocations(current!.warehouse_id),
    enabled: mode === 'putaway' && !!current,
  })
  const fill = useQuery({
    queryKey: ['warehouse', 'stats', current?.warehouse_id],
    queryFn: () => api.warehouse.getWarehouseStats(current!.warehouse_id),
    enabled: mode === 'putaway' && !!current,
  })
  const openById = new Map((fill.data?.locations ?? []).map((loc) => [loc.location_id, loc]))
  const placeable = (locations.data ?? []).filter((loc) => {
    if (!loc.is_active || loc.location_type === 'dock' || loc.location_type === 'zone') return false
    const occ = openById.get(loc.id)
    return !occ || occ.open
  })
  const suggestion = useQuery({
    queryKey: ['warehouse', 'suggest', current?.stock_item_id],
    queryFn: () => api.warehouse.suggestLocation(current!.stock_item_id),
    enabled: mode === 'putaway' && !!current,
  })

  useEffect(() => {
    if (suggestion.data?.id) setLocationId(suggestion.data.id)
  }, [suggestion.data?.id])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['warehouse', 'tasks'] })
    qc.invalidateQueries({ queryKey: ['warehouse', 'board'] })
  }

  const run = useMutation({
    mutationFn: async (action: 'claim' | 'complete' | 'block' | 'release') => {
      if (!current) return
      if (action === 'claim') return api.warehouse.claimTask(current.id)
      if (action === 'release') return api.warehouse.releaseTask(current.id)
      if (action === 'block') return api.warehouse.blockTask(current.id, reason.trim())
      const body: CompleteWarehouseTaskBody = {}
      if (notes.trim()) body.notes = notes.trim()
      if (mode === 'receive' || mode === 'handover' || mode === 'pick') {
        const q = Number(quantity)
        if (!Number.isFinite(q) || q <= 0) throw new HttpError({ code: 'VALIDATION', message: t('posts.quantity') })
        body.quantity = q
      }
      if (mode === 'handover' && reason.trim()) body.reason = reason.trim()
      if (mode === 'putaway') body.location_id = locationId
      if (mode === 'control') {
        body.decision = decision
        if (newQty.trim()) body.new_quantity = Number(newQty)
      }
      return api.warehouse.completeTask(current.id, body)
    },
    onSuccess: () => {
      setFormError(null)
      invalidate()
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : t('posts.load_error'))
    },
  })

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)_280px]">
      <section className="rounded-2xl border border-border bg-card p-3">
        <h2 className="px-2 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('posts.queue')}
        </h2>
        {tasks.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : tasks.isError ? (
          <p className="flex items-center gap-2 px-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {t('posts.load_error')}
          </p>
        ) : list.length === 0 ? (
          <p className="px-2 py-6 text-sm text-muted-foreground">{t('posts.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {list.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  onClick={() => setSelected(task.id)}
                  className={cn(
                    'flex min-h-[44px] w-full flex-col items-start rounded-xl px-3 py-2 text-left',
                    current?.id === task.id ? 'bg-primary/10' : 'hover:bg-muted',
                  )}
                >
                  <span className="font-mono text-xs tabular-nums">{task.stock_ref}</span>
                  <span className="line-clamp-2 text-sm">{task.stock_description}</span>
                  {task.status === 'blocked' ? (
                    <span className="text-xs text-amber-700 dark:text-amber-300">{t('posts.blocked')}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card p-4">
        {current ? <TaskBody task={current} /> : <p className="text-sm text-muted-foreground">{t('posts.empty')}</p>}
      </section>

      <section className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4">
        {current && allowed ? (
          <>
            {mode === 'receive' || mode === 'handover' || mode === 'pick' ? (
              <label className="flex flex-col gap-1 text-sm">
                {t('posts.quantity')}
                <Input inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              </label>
            ) : null}
            {mode === 'putaway' ? (
              <label className="flex flex-col gap-1 text-sm">
                {t('posts.location')}
                <select
                  className="min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  <option value="">{t('posts.no_location')}</option>
                  {(placeable).map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {locLabel(loc)}
                      {openById.get(loc.id) ? ` · ${Math.round(openById.get(loc.id)!.percent)}%` : ''}
                      {suggestion.data?.id === loc.id ? ` — ${t('posts.suggested')}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {mode === 'control' ? (
              <>
                <label className="flex flex-col gap-1 text-sm">
                  {t('posts.decision')}
                  <select
                    className="min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
                    value={decision}
                    onChange={(e) => setDecision(e.target.value as CompleteWarehouseTaskBody['decision'])}
                  >
                    <option value="in_stock">{t('posts.in_stock')}</option>
                    <option value="damaged">{t('posts.damaged')}</option>
                    <option value="lost">{t('posts.lost')}</option>
                    <option value="return_receive">{t('posts.return_receive')}</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  {t('posts.new_quantity')}
                  <Input inputMode="decimal" value={newQty} onChange={(e) => setNewQty(e.target.value)} />
                </label>
              </>
            ) : null}
            <label className="flex flex-col gap-1 text-sm">
              {t('posts.notes')}
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            {mode !== 'control' ? (
              <label className="flex flex-col gap-1 text-sm">
                {t('posts.reason')}
                <Input value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
            ) : null}
            {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
            <Button
              type="button"
              className="min-h-[44px]"
              disabled={run.isPending || current.status === 'blocked'}
              onClick={() => run.mutate('complete')}
            >
              {t('posts.complete')}
            </Button>
            {current.status === 'pending' ? (
              <Button type="button" variant="secondary" className="min-h-[44px]" onClick={() => run.mutate('claim')}>
                {t('posts.claim')}
              </Button>
            ) : null}
            {mode !== 'control' && current.status !== 'blocked' ? (
              <Button
                type="button"
                variant="outline"
                className="min-h-[44px]"
                disabled={!reason.trim() || run.isPending}
                onClick={() => run.mutate('block')}
              >
                {t('posts.block')}
              </Button>
            ) : null}
            {canAssign && current.status === 'in_progress' ? (
              <Button type="button" variant="ghost" className="min-h-[44px]" onClick={() => run.mutate('release')}>
                {t('posts.release')}
              </Button>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{current ? t('posts.empty') : t('posts.empty')}</p>
        )}
      </section>
    </div>
  )
}

function TaskBody({ task }: { task: WarehouseTask }) {
  const { t } = useTranslation('warehouse')
  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="font-mono text-sm tabular-nums text-muted-foreground">{task.stock_ref}</p>
        <h2 className="text-xl font-semibold">{task.stock_description || task.stock_ref}</h2>
      </div>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-muted-foreground">{t('posts.quantity')}</dt>
          <dd className="font-mono tabular-nums">
            {task.quantity_current ?? '—'} {task.unit}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('posts.blocked')}</dt>
          <dd>{task.status === 'blocked' ? task.blocked_reason || t('posts.blocked') : '—'}</dd>
        </div>
      </dl>
      {task.notes ? <p className="text-sm text-muted-foreground">{task.notes}</p> : null}
    </div>
  )
}

function locLabel(loc: WarehouseLocation) {
  return [loc.zone, loc.code].filter(Boolean).join(' · ')
}
